import { catchCalculatorError, resultListFilter } from "@component/assets/AbstractListingCalculator/domain";
import type { ResultListResponse } from "@component/assets/AbstractListingCalculator/types";
import { Config } from "@config";
import { formatText } from "@ui/format";
import type { StringPair } from "@utils/JsonDataBuilder";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import { http } from "bm-ui-kit";
import type { SmeLoanLeadCalcResponse } from "../LeadForm/types";
import type { SmeLoanLeadFormWithCurrencyInputsCalcRequest } from "../LeadFormWithCurrencyInputs/SmeLoanLeadFormWithCurrencyInputs";
import type { OptionResponse, SmeLoanRequest, SmeLoanResponseItem, SmeLoanSearchParams, SmeLoanSort } from "./types";

export function searchParamsToRequest(searchParams: SmeLoanSearchParams, sort: SmeLoanSort = "NAME"): SmeLoanRequest {
	const { selectedBanks, ...other } = searchParams;

	const request: SmeLoanRequest = {
		selectedBanks:
			selectedBanks.length === 0
				? undefined
				: typeof selectedBanks === "string"
					? selectedBanks
					: selectedBanks.join(","),
		companyFoundYear: searchParams.companyStartYear,
		comparator: sort,
		sort,
		roleCode: "BM",
		...other,
	};

	return request;
}

export async function calculate(request: SmeLoanRequest): Promise<any> {
	return http.get(Config.getApiUrl("/api/public/corporate-loan", request)).catch(catchCalculatorError);
}

export function responseToResult(response: any): ResultListResponse<SmeLoanResponseItem> {
	return resultListFilter(response);
}

export function getLeadCalculatorRequest(
	searchParams: SmeLoanSearchParams,
): SmeLoanLeadFormWithCurrencyInputsCalcRequest {
	const otherData = new JsonDataBuilder();
	otherData.addItem("Hitelösszeg", "loanAmount", searchParams.loanAmount, "ft");
	otherData.addItem("Hitel típusa", "loanType", searchParams.loanType);
	otherData.addItem("Alapítás éve", "companyStartYear", searchParams.companyStartYear);

	return {
		companyFoundYear: searchParams.companyStartYear,
		loanAmount: searchParams.loanAmount,
		otherDataJson: otherData.getJson(),
	};
}

export function getLeadCalculatorResponse(
	response: SmeLoanResponseItem,
	_searchParams: SmeLoanSearchParams,
): SmeLoanLeadCalcResponse {
	return {
		bankCode: response?.bank?.code,
		bankName: response?.bank?.name,
		productName: response?.name,
		id: response?.id,
	};
}

export async function getFormOptions(): Promise<OptionResponse[]> {
	return http.getWithCache(Config.getApiUrl("/api/public/corporate-loan/loan-types"));
}

const LOAN_GOALS = {
	BANK_ACCOUNT_LOAN: "Folyószámlahitel",
	CURRENT_ASSET_NON_REVOLVING_LOAN: "Forgóeszköz hitel",
	CURRENT_ASSET_REVOLVING_LOAN: "Forgóeszköz hitel (rulírozó)",
	FREE_CONSUMPTION_LOAN: "Szabadfelhasználású hitel",
	INVESTMENT_LOAN: "Beruházási hitel",
};

export class SmeLoanInfo {
	public static getSearchParametersText(request: SmeLoanRequest): StringPair[] {
		const data = new JsonDataBuilder();
		data.addTextItem("Hitelösszeg", request.loanAmount, "ft");
		data.addTextItem("Hitel típusa", LOAN_GOALS[request.loanType]);
		data.addTextItem("Alapítás éve", request.companyStartYear);

		return data.getDataArray();
	}

	public static getProductParametersText(_request: SmeLoanRequest, _response: SmeLoanResponseItem): StringPair[] {
		return [];
	}
}

export function creatCreditLineValue(responseItem: SmeLoanResponseItem): string {
	const { maxCreditLineValue, minCreditLineValue } = responseItem;
	let creditLineValue = `${formatText(minCreditLineValue, "ft")} - ${formatText(maxCreditLineValue, "ft")}`;

	if (maxCreditLineValue === 1_000_000_000) {
		creditLineValue = "egyedi elbírálás alapján";
	}

	return creditLineValue;
}

export function createInterest(responseItem: SmeLoanResponseItem): string {
	const {
		lowestInterest: { interestString, interestValue },
	} = responseItem;
	let fullString = "egyedi";

	if (interestString) {
		fullString = interestString;

		if (Number(interestValue) > 0) {
			fullString += ` (${formatText(interestValue, "%")})`;
		}
	}

	return fullString;
}

export function getEconomicHistoryText(responseItem: SmeLoanResponseItem): string {
	if (responseItem === undefined) return "";

	return !responseItem.economicHistory ? "nem elvárás" : responseItem.economicHistory;
}
